import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Mail,
  Plus,
  Copy,
  Check,
  Trash2,
  Eye,
  RefreshCw,
  MapPin
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EmailTrackingPage = () => {
  const { getAuthHeader } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newSent, setNewSent] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignDetails, setCampaignDetails] = useState(null);
  const [trackingCode, setTrackingCode] = useState(null);
  const [copiedCode, setCopiedCode] = useState(false);

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/email-campaigns`, {
        headers: getAuthHeader()
      });
      setCampaigns(response.data);
    } catch (error) {
      toast.error("Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName) {
      toast.error("Please enter a campaign name");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/email-campaigns`, 
        { name: newName, total_sent: newSent },
        { headers: getAuthHeader() }
      );
      toast.success("Campaign created!");
      setDialogOpen(false);
      setNewName("");
      setNewSent(0);
      fetchCampaigns();
    } catch (error) {
      toast.error("Failed to create campaign");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (campaignId) => {
    try {
      await axios.delete(`${API}/email-campaigns/${campaignId}`, {
        headers: getAuthHeader()
      });
      toast.success("Campaign deleted");
      fetchCampaigns();
      if (selectedCampaign === campaignId) {
        setSelectedCampaign(null);
        setCampaignDetails(null);
      }
    } catch (error) {
      toast.error("Failed to delete campaign");
    }
  };

  const handleViewDetails = async (campaignId) => {
    setSelectedCampaign(campaignId);
    try {
      const [detailsRes, codeRes] = await Promise.all([
        axios.get(`${API}/email-campaigns/${campaignId}`, { headers: getAuthHeader() }),
        axios.get(`${API}/email-campaigns/${campaignId}/tracking-code`, { headers: getAuthHeader() })
      ]);
      setCampaignDetails(detailsRes.data);
      setTrackingCode(codeRes.data);
    } catch (error) {
      toast.error("Failed to load campaign details");
    }
  };

  const copyCode = async () => {
    if (!trackingCode) return;
    try {
      await navigator.clipboard.writeText(trackingCode.html_code);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
      toast.success("Tracking code copied!");
    } catch (error) {
      toast.info(`Code: ${trackingCode.html_code}`);
    }
  };

  const getOpenRate = (campaign) => {
    if (!campaign.total_sent || campaign.total_sent === 0) return "N/A";
    return `${((campaign.unique_opens / campaign.total_sent) * 100).toFixed(1)}%`;
  };

  return (
    <div className="space-y-6" data-testid="email-tracking-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-mono font-bold tracking-tight flex items-center gap-3">
            <Mail className="w-7 h-7 text-primary" />
            Email Open Tracking
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Track when your emails are opened
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchCampaigns} className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-2" data-testid="create-campaign-btn">
                <Plus className="w-4 h-4" />
                New Campaign
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border">
              <DialogHeader>
                <DialogTitle>Create Email Campaign</DialogTitle>
                <DialogDescription>
                  Create a tracking campaign to monitor email opens.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="space-y-2">
                  <Label>Campaign Name</Label>
                  <Input
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="e.g., January Newsletter"
                    className="bg-background"
                    data-testid="campaign-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Total Emails Sent (optional)</Label>
                  <Input
                    type="number"
                    value={newSent}
                    onChange={(e) => setNewSent(parseInt(e.target.value) || 0)}
                    placeholder="For open rate calculation"
                    className="bg-background"
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting}>
                    Create Campaign
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Info */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="p-4">
          <p className="text-sm">
            <strong>How it works:</strong> Create a campaign, copy the tracking pixel, and paste it into your email HTML. 
            When recipients open the email, you'll see their IP, location, and device info here.
          </p>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Campaigns List */}
        <Card className="bg-card/50 border-border" data-testid="campaigns-list">
          <CardHeader>
            <CardTitle className="text-sm font-medium">Your Campaigns</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-2">
                {[...Array(3)].map((_, i) => <div key={i} className="skeleton h-16 w-full" />)}
              </div>
            ) : campaigns.length > 0 ? (
              <div className="space-y-2">
                {campaigns.map((campaign) => (
                  <div
                    key={campaign.id}
                    className={`p-4 bg-background rounded border cursor-pointer transition-colors ${
                      selectedCampaign === campaign.id ? "border-primary" : "border-border hover:border-primary/50"
                    }`}
                    onClick={() => handleViewDetails(campaign.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium">{campaign.name}</p>
                        <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            {campaign.unique_opens} opens
                          </span>
                          <span>Rate: {getOpenRate(campaign)}</span>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-red-500"
                        onClick={(e) => { e.stopPropagation(); handleDelete(campaign.id); }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Mail className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No campaigns yet</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Campaign Details */}
        <Card className="bg-card/50 border-border" data-testid="campaign-details">
          <CardHeader>
            <CardTitle className="text-sm font-medium">Campaign Details</CardTitle>
          </CardHeader>
          <CardContent>
            {campaignDetails ? (
              <div className="space-y-4">
                <div className="p-3 bg-background rounded border border-border">
                  <p className="font-medium">{campaignDetails.campaign.name}</p>
                  <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                    <div>
                      <p className="text-2xl font-mono font-bold text-primary">{campaignDetails.unique_opens}</p>
                      <p className="text-xs text-muted-foreground">Unique Opens</p>
                    </div>
                    <div>
                      <p className="text-2xl font-mono font-bold">{campaignDetails.total_opens}</p>
                      <p className="text-xs text-muted-foreground">Total Opens</p>
                    </div>
                    <div>
                      <p className="text-2xl font-mono font-bold">{getOpenRate(campaignDetails.campaign)}</p>
                      <p className="text-xs text-muted-foreground">Open Rate</p>
                    </div>
                  </div>
                </div>

                {/* Tracking Code */}
                {trackingCode && (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Tracking Pixel</p>
                    <div className="relative">
                      <pre className="bg-background p-3 rounded border border-border text-xs font-mono overflow-x-auto">
                        {trackingCode.html_code}
                      </pre>
                      <Button
                        variant="outline"
                        size="sm"
                        className="absolute top-2 right-2 gap-1"
                        onClick={copyCode}
                      >
                        {copiedCode ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                        Copy
                      </Button>
                    </div>
                  </div>
                )}

                {/* Recent Opens */}
                {campaignDetails.opens && campaignDetails.opens.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Recent Opens</p>
                    <div className="space-y-2 max-h-[250px] overflow-y-auto">
                      {campaignDetails.opens.slice(0, 10).map((open, i) => (
                        <div key={i} className="p-2 bg-background rounded border border-border text-sm">
                          <div className="flex items-center justify-between">
                            <span className="font-mono text-primary">{open.ip_address}</span>
                            {open.is_vpn && <Badge className="bg-amber-500/10 text-amber-500 text-[10px]">VPN</Badge>}
                          </div>
                          <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                            <MapPin className="w-3 h-3" />
                            {open.city && open.country ? `${open.city}, ${open.country}` : open.country || "Unknown"}
                            <span>•</span>
                            {open.browser} / {open.device}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            {new Date(open.timestamp).toLocaleString()}
                            {open.open_count > 1 && ` (${open.open_count}x)`}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Eye className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Select a campaign to view details</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EmailTrackingPage;
